import { DateTime } from "luxon";

export const byStartDate = (
  { startDate: a }: { startDate: string },
  { startDate: b }: { startDate: string },
) => DateTime.fromISO(a).toMillis() - DateTime.fromISO(b).toMillis();

export const byStartDateTime = (
  { startDateTime: a }: { startDateTime: string },
  { startDateTime: b }: { startDateTime: string },
) => DateTime.fromISO(a).toMillis() - DateTime.fromISO(b).toMillis();

/**
 * Formats a date range based on specific conditions:
 * - If both dates are the same day, displays the date as "dd LLL yyyy".
 * - If both dates are in the same month, displays the day changes as "dd - dd
 * LLL yyyy".
 * - If both dates are in the same year, displays the day changes as "dd LLL -
 * dd LLL yyyy".
 * - For other cases, displays the range as "dd LLL yyyy - dd LLL yyyy".
 *
 * @param {DateTime} startDateTime - The start date and time.
 * @param {DateTime} endDateTime - The end date and time.
 * @returns {string} The formatted date range string.
 */
export const formatDateRange = (
  startDateString: string,
  endDateString: string,
): string => {
  const startDate = DateTime.fromISO(startDateString).startOf("day");
  const endDate = DateTime.fromISO(endDateString).startOf("day");

  // Check if both dates are the same day
  if (startDate.hasSame(endDate, "day")) {
    return startDate.toFormat("EEE dd LLL yyyy");
  }

  // Check if both dates are in the same month
  if (startDate.hasSame(endDate, "month")) {
    const formattedStartDate = startDate.toFormat("EEE dd");
    const formattedEndDate = endDate.toFormat("EEE dd LLL yyyy");
    return `${formattedStartDate} - ${formattedEndDate}`;
  }

  // Check if both dates are in the same year
  if (startDate.hasSame(endDate, "year")) {
    const formattedStartDate = startDate.toFormat("EEE dd LLL");
    const formattedEndDate = endDate.toFormat("EEE dd LLL yyyy");
    return `${formattedStartDate} - ${formattedEndDate}`;
  }

  // General case
  return `${startDate.toFormat("EEE dd LLL yyyy")} - ${endDate.toFormat(
    "EEE dd LLL yyyy",
  )}`;
};

export const formatDateTimeRange = (
  startDateTimeString: string,
  endDateTimeString: string,
): string => {
  const startDateTime = DateTime.fromISO(startDateTimeString);
  const endDateTime = DateTime.fromISO(endDateTimeString);

  const formattedStartDateTime = startDateTime.toFormat(
    "EEE dd LLL yyyy, h:mm a",
  );
  const formattedEndDateTime = startDateTime.hasSame(endDateTime, "day")
    ? endDateTime.toFormat("h:mm a")
    : endDateTime.toFormat("EEE dd LLL yyyy, h:mm a");

  return `${formattedStartDateTime} - ${formattedEndDateTime}`;
};

export const calcDurationDisplay = (
  startDateTimeString: string,
  endDateTimeString: string,
): string => {
  const startDateTime = DateTime.fromISO(startDateTimeString);
  const endDateTime = DateTime.fromISO(endDateTimeString);
  const duration = endDateTime.diff(startDateTime);

  if (duration.as("days") > 1) {
    return duration.toFormat("d 'days' h 'hours' m 'minutes'");
  } else if (duration.as("hours") > 1) {
    return duration.toFormat("h 'hours' m 'minutes'");
  } else {
    return duration.toFormat("m 'minutes'");
  }
};

export const calcInclusiveDurationInDaysDisplay = (
  startDateTimeString: string,
  endDateTimeString: string,
): string => {
  const startDateTime = DateTime.fromISO(startDateTimeString);
  const endDateTime = DateTime.fromISO(endDateTimeString);
  const daysInclusive =
    Math.floor(endDateTime.diff(startDateTime).as("days")) + 1;

  if (daysInclusive === 1) {
    return `${daysInclusive} day`;
  } else {
    return `${daysInclusive} days`;
  }
};

// When comparing two [DateTime] objects, if they fall on the same day but at
// different times, the result should always be considered as being on the same
// day. This rule holds true regardless of the specific time values.
//
// e.g. if the times are 10 PM and 10 AM on the same day, the comparison should
// return [true] regardless of the order in which they are compared.
export const isSameDayOrBefore = (a: DateTime, b: DateTime) =>
  a.hasSame(b, "day") || a <= b;

export const displayDateTime = (s: string) =>
  DateTime.fromISO(s).toFormat("d LLL yyyy h:mm a");
